import { useEffect, useRef, useState } from 'react';
import { getJson } from '@zapier/toolbox-browser-fetch';
import { useTrackingContext } from '@zapier/toolbox-tracking';

import useLocalStorage from '../../hooks/useLocalStorage';
import { usePopupSurvey } from './usePopupSurvey';
import { Survey } from './Survey';
import { SplitioProvider } from '../SplitioProvider';

const POPUP_SURVEY_LOCALSTORAGE_KEY = 'zapier__popup-survey';
const EXPERIMENT_NAME = 'popup-survey';
const SURVEY_EVENTS = {
  shown: 'shown',
  dismissed: 'dismissed',
  completed: 'completed',
};

const SurveyContainerUnwrapped = () => {
  const hasSurveyAlreadyAppeared = useRef(false);
  const [showPopup, setShowPopup] = useState(false);
  const [userHasSignedUp, setUserHasSignedUp] = useState(true);
  const [surveySentSuccessfully, setSurveySentSuccessfully] = useState(false);
  const [showPopupFromLocalStorage, setShowPopupFromLocalStorage] =
    useLocalStorage(POPUP_SURVEY_LOCALSTORAGE_KEY);

  const { trackEvent } = useTrackingContext();

  async function getUserHasSignedUp() {
    try {
      const { has_signed_up: hasSignedUP } = await getJson('/api/v4/identity/');
      setUserHasSignedUp(hasSignedUP);
    } catch (e) {
      setUserHasSignedUp(false);
    }
  }

  useEffect(() => {
    getUserHasSignedUp();
  }, []);

  useEffect(() => {
    if (showPopupFromLocalStorage === null && !userHasSignedUp) {
      setShowPopup(true);
    }
  }, [showPopupFromLocalStorage, userHasSignedUp]);

  function trackOnMount() {
    if (!hasSurveyAlreadyAppeared.current) {
      trackEvent(`${EXPERIMENT_NAME}:${SURVEY_EVENTS.shown}`);
    }

    hasSurveyAlreadyAppeared.current = true;
  }

  function hidePopup() {
    setShowPopupFromLocalStorage(false);
    setShowPopup(false);
  }

  function dismissPopup() {
    hidePopup();
    trackEvent(`${EXPERIMENT_NAME}:${SURVEY_EVENTS.dismissed}`);
  }

  function handleSubmission(response: string) {
    trackEvent(`${EXPERIMENT_NAME}:${SURVEY_EVENTS.completed}`, {
      response,
    });
    setSurveySentSuccessfully(true);
  }

  function closeOnSuccess() {
    hidePopup();
  }

  const popupSurvey = usePopupSurvey();

  /**
   * Display survey if not already dismissed AND feature flag is enabled
   */
  const displaySurvey =
    showPopup && popupSurvey.isReady && popupSurvey.isActive;

  // After integrating with Split.io, this would work. Currently will be always hidden
  if (!displaySurvey) {
    return null;
  }

  return (
    <Survey
      dismissPopup={dismissPopup}
      dismissPopupOnClose={closeOnSuccess}
      onMount={trackOnMount}
      onSubmit={handleSubmission}
      surveySentSuccessfully={surveySentSuccessfully}
    />
  );
};

const SurveyContainer = () => (
  <SplitioProvider>
    <SurveyContainerUnwrapped />
  </SplitioProvider>
);
export { SurveyContainer };
